import { MaestroAPI, maestroApiCall } from '@michelin/central-provider';
import hash from 'object-hash';
import { useQuery } from 'react-query';
import { apigatewayBaseRoute as baseURL } from 'services/restApiConst';
import { CountriesType, LocationType, PartPrices, PqlPriceType } from 'types/UFOTypes';
import { getMspn } from 'utils/getMspn';

const CACHE_TIME = 15 * 60 * 1000;

interface PartQty {
  mspn: string;
  qty: number;
  is_pql: boolean;
}

interface GetArrayPricesParams {
  mode: LocationType;
  fleet_id: string;
  provider_id: string;
  price_date?: string;
  parts: PartQty[];
  country_code?: CountriesType;
  state?: string;
  city?: string;
  zip_code?: string;
}

export interface PartPricesResponse {
  customer_id: string;
  delivery_fees: string;
  delivery_local_tax: string;
  parts: PartPrices[];
  pqlParts: PqlPriceType[];
  requested_at: number;
}

const getPrices: MaestroAPI = { method: 'POST', endpoint: 'prices', baseURL };
const pricesCache = new Map<string, { data: PartPrices; time: number }>();
const pqlPricesCache = new Map<string, { data: PqlPriceType; time: number }>();
// eslint-disable-next-line @typescript-eslint/naming-convention
const deliveryTaxes = { delivery_fees: '0', delivery_local_tax: '0' };

const getCommonParams = (payload: GetArrayPricesParams) => ({
  mode: payload.mode,
  fleet_id: payload.fleet_id,
  provider_id: payload.provider_id,
  price_date: payload.price_date,
  country_code: payload.country_code,
  state: payload.state,
  city: payload.city,
  zip_code: payload.zip_code,
});

function cacheKey(payload: GetArrayPricesParams, part: PartQty): string {
  return hash({ payload: getCommonParams(payload), part });
}

let bulkRequestsTimer: NodeJS.Timeout | null = null;

let bulkRequests: {
  payload: GetArrayPricesParams;
  onPartsCached: () => void;
  reject: (error: any) => void;
}[] = [];

function findPart(part: PartQty, pricesRes: PartPricesResponse) {
  return pricesRes.parts.find((price) => getMspn(price) === getMspn(part) && Number(price.qty) === part.qty);
}

function findPqlPart(part: PartQty, pricesRes: PartPricesResponse) {
  return pricesRes.pqlParts.find((price) => getMspn(price) === getMspn(part) && Number(price.qty) === part.qty);
}

function mergeParts(parts: PartQty[], newParts: PartQty[]) {
  const mergedParts: PartQty[] = parts;
  newParts.forEach((p) => {
    if (!mergedParts.some((mp) => getMspn(mp) === getMspn(p))) mergedParts.push(p);
  });
  return mergedParts;
}

async function bulkPricesLoader() {
  bulkRequestsTimer = null;
  const payload: GetArrayPricesParams = { ...bulkRequests[0].payload };
  payload.parts = [];
  const br = bulkRequests;
  bulkRequests = [];
  try {
    br.forEach((b) => {
      payload.parts = mergeParts(payload.parts, b.payload.parts);
    });
    const res: PartPricesResponse = await maestroApiCall(getPrices, payload);
    br.forEach((b) => {
      b.payload.parts.forEach((p) => {
        const foundPart = findPart(p, res);
        const foundPqlPart = findPqlPart(p, res);
        // if (!foundPart && br.length === 1 && res.parts.length === 1) foundPart = res.parts.shift();
        if (foundPart) pricesCache.set(cacheKey(payload, p), { data: foundPart, time: Date.now() });
        if (foundPqlPart) pqlPricesCache.set(cacheKey(payload, p), { data: foundPqlPart, time: Date.now() });
      });
    });
    deliveryTaxes.delivery_fees = res.delivery_fees;
    deliveryTaxes.delivery_local_tax = res.delivery_local_tax;

    const x = new Map();
    br.forEach((b) => {
      if (x.has(b.onPartsCached)) return;
      x.set(b.onPartsCached, true);
      b.onPartsCached();
    });
  } catch (e) {
    br.forEach(({ reject }) => reject(e));
  }
}

function compatiblePayload(payload: GetArrayPricesParams) {
  if (bulkRequests.length === 0) return true;
  if (hash(getCommonParams(payload)) !== hash(getCommonParams(bulkRequests[0].payload))) return false;
  return true;
}

async function getPricesWithCache(payload: GetArrayPricesParams) {
  if (payload.parts.length === 0) {
    return {
      customer_id: payload.fleet_id,
      parts: [],
      pqlParts: [],
      requested_at: Date.now(),

      ...deliveryTaxes,
    } as PartPricesResponse;
  }

  return new Promise<PartPricesResponse>((resolve, reject) => {
    const onPartsCached = () => {
      resolve({
        customer_id: payload.fleet_id,
        parts: payload.parts
          .filter((p) => p.mspn)
          .map((p) => pricesCache.get(cacheKey(payload, p))?.data)
          .filter((p) => !!p),
        pqlParts: payload.parts.map((p) => pqlPricesCache.get(cacheKey(payload, p))?.data).filter((p) => !!p),
        requested_at: Date.now(),
        ...deliveryTaxes,
      } as PartPricesResponse);
    };

    if (!compatiblePayload(payload))
      bulkPricesLoader().catch((_) => {
        // TODO: better handle error
        console.error('An error occurred while loading bulk prices!');
      });

    let shouldWait = false;
    payload.parts.forEach((p) => {
      const onePartPayload = { ...payload, parts: [p] };
      const key = cacheKey(payload, p);
      if ((pricesCache.get(key)?.time || 0) >= Date.now() - CACHE_TIME) return;
      if ((pqlPricesCache.get(key)?.time || 0) >= Date.now() - CACHE_TIME) return;
      bulkRequests.push({ payload: onePartPayload, onPartsCached, reject });
      shouldWait = true;
    });
    if (shouldWait) {
      if (bulkRequestsTimer) clearTimeout(bulkRequestsTimer);
      bulkRequestsTimer = setTimeout(bulkPricesLoader, 50);
    } else {
      onPartsCached();
    }
  });
}

export function useGetPrices(payLoad: GetArrayPricesParams | null) {
  return useQuery<PartPricesResponse | null>(['get-prices', hash(payLoad)], () => {
    if (!payLoad) return null;
    return getPricesWithCache(payLoad);
  });
}
